if (!customElements.get('product-sticky-cta')) {
  customElements.define(
    'product-sticky-cta',
    class extends HTMLElement {
      constructor() {
        super();
        this.formId = this.dataset.formId;
        this.sourceButtonId = this.dataset.sourceButtonId;
        this.priceEl = this.querySelector('[data-sticky-price]');
        this.compareEl = this.querySelector('[data-sticky-compare]');
        this.badgeEl = this.querySelector('[data-sticky-badge]');
        this.imageEl = this.querySelector('.product-sticky-cta__image');
        this.buttons = this.querySelectorAll('button[type="submit"]');
        this.unsubVariant = null;
        this.scrollTicking = false;
        this.boundComputeAnchor = this.computeDesktopAnchor.bind(this);
        this.boundOnScroll = this.onScroll.bind(this);
        this.boundSyncSupportWidget = this.syncSupportWidget.bind(this);
      }

      connectedCallback() {
        // Inherit color-scheme classes from the source section (lost during portal).
        if (!this.classList.contains('gradient')) {
          const sourceBtn = document.getElementById(this.sourceButtonId);
          if (sourceBtn) {
            let p = sourceBtn.parentElement;
            while (p && p !== document.body) {
              const cls = p.className.toString();
              const match = cls.match(/color-[\w-]+/);
              if (match) {
                this.classList.add(match[0], 'gradient');
                break;
              }
              p = p.parentElement;
            }
          }
        }
        // Portal to body to avoid clipping by ancestors with transform/overflow.
        if (this.parentElement && this.parentElement !== document.body) {
          document.body.appendChild(this);
          return; // re-entrant connectedCallback will run setup
        }
        // iOS Chrome keeps a persistent bottom toolbar that already covers the
        // home-indicator safe-area, so env(safe-area-inset-bottom) padding is
        // redundant and leaves a visible gap above the toolbar. iOS Safari
        // hides its toolbar on scroll, exposing the home indicator — there the
        // safe-area padding is needed. Tag iOS Chrome so CSS can drop the
        // extra padding only there.
        if (/CriOS/.test(navigator.userAgent)) {
          this.classList.add('is-ios-chrome');
        }
        this.attachToggle();
        this.attachVariantSubscriber();
        window.addEventListener('resize', this.boundComputeAnchor);
        window.addEventListener('resize', this.boundSyncSupportWidget);
        if (window.visualViewport) {
          window.visualViewport.addEventListener('resize', this.boundSyncSupportWidget);
          window.visualViewport.addEventListener('scroll', this.boundSyncSupportWidget);
        }
        this.computeDesktopAnchor();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.boundOnScroll);
        if (this.unsubVariant) this.unsubVariant();
        window.removeEventListener('resize', this.boundComputeAnchor);
        window.removeEventListener('resize', this.boundSyncSupportWidget);
        if (window.visualViewport) {
          window.visualViewport.removeEventListener('resize', this.boundSyncSupportWidget);
          window.visualViewport.removeEventListener('scroll', this.boundSyncSupportWidget);
        }
        this.resetSupportWidget();
        document.body.classList.remove('has-sticky-cta-visible');
      }

      attachToggle() {
        if (!document.getElementById(this.sourceButtonId)) return;
        window.addEventListener('scroll', this.boundOnScroll, { passive: true });
        this.update(); // initial state
      }

      onScroll() {
        if (this.scrollTicking) return;
        this.scrollTicking = true;
        requestAnimationFrame(() => {
          this.update();
          this.scrollTicking = false;
        });
      }

      update() {
        const sourceBtn = document.getElementById(this.sourceButtonId);
        if (!sourceBtn) return;
        const scrolledPast = sourceBtn.getBoundingClientRect().bottom < 0;
        this.toggleVisible(scrolledPast);
      }

      toggleVisible(state) {
        this.classList.toggle('is-visible', state);
        document.body.classList.toggle('has-sticky-cta-visible', state);
        // Sync immediately for the hide path; on show, wait for the 150ms slide-up
        // transition so getBoundingClientRect() reflects the final position, not the
        // mid-animation transform.
        if (state) {
          setTimeout(this.boundSyncSupportWidget, 180);
        } else {
          this.syncSupportWidget();
        }
      }

      // Pin the third-party WhatsApp widget (#carthike-chat-button-container) just above the
      // visible sticky CTA pill. Anchoring to the button (not the wrapper) avoids drift from
      // env(safe-area-inset-bottom) padding on iPhone real, and the visualViewport listeners
      // keep the gap constant when Safari iOS shows/hides its bottom toolbar.
      syncSupportWidget() {
        const wa = document.getElementById('carthike-chat-button-container');
        if (!wa) return;
        if (!this.classList.contains('is-visible')) {
          this.resetSupportWidget();
          return;
        }
        // Detect via the wrapper, not the button: on desktop the .__mobile wrapper is
        // display:none but the button retains display:inline-flex of its own, so checking
        // the button's computed display falsely picks it as anchor and its rect collapses
        // to {0,0,0,0}, pushing WA off-screen above the viewport.
        const mobileWrap = this.querySelector('.product-sticky-cta__mobile');
        const desktopCard = this.querySelector('.product-sticky-cta__desktop');
        const mobileActive = mobileWrap && getComputedStyle(mobileWrap).display !== 'none';
        const mobileBtn = mobileActive ? this.querySelector('.product-sticky-cta__button--mobile') : null;
        const anchor = mobileBtn || desktopCard;
        if (!anchor) return;
        const anchorRect = anchor.getBoundingClientRect();
        const vh = (window.visualViewport && window.visualViewport.height) || window.innerHeight;
        const GAP = 10; // tight visual gap — WA sits just above the ATC pill
        const bottomPx = Math.max(0, Math.round(vh - anchorRect.top + GAP));
        wa.style.setProperty('bottom', bottomPx + 'px', 'important');
      }

      resetSupportWidget() {
        const wa = document.getElementById('carthike-chat-button-container');
        if (wa) wa.style.removeProperty('bottom');
      }

      attachVariantSubscriber() {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
        this.unsubVariant = subscribe(PUB_SUB_EVENTS.variantChange, (payload) => {
          const variant = payload && payload.data && payload.data.variant;
          if (!variant) return;
          this.updateForVariant(variant);
        });
      }

      updateForVariant(variant) {
        if (this.priceEl) this.priceEl.innerHTML = this.formatMoney(variant.price);
        const hasDiscount = variant.compare_at_price && variant.compare_at_price > variant.price;
        if (this.compareEl) {
          if (hasDiscount) {
            this.compareEl.innerHTML = this.formatMoney(variant.compare_at_price);
            this.compareEl.style.display = '';
          } else {
            this.compareEl.style.display = 'none';
          }
        }
        if (this.badgeEl) {
          if (hasDiscount) {
            const pct = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
            this.badgeEl.textContent = `-${pct}%`;
            this.badgeEl.style.display = '';
          } else {
            this.badgeEl.style.display = 'none';
          }
        }
        if (this.imageEl && variant.featured_image && variant.featured_image.src) {
          this.imageEl.src = variant.featured_image.src;
        }
        this.buttons.forEach((b) => {
          b.disabled = !variant.available;
        });
      }

      formatMoney(cents) {
        if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
          return window.Shopify.formatMoney(cents, window.Shopify.moneyFormat);
        }
        const format = (window.Shopify && window.Shopify.moneyFormat) || '${{amount}}';
        return this.formatWithShopifyString(cents, format);
      }

      formatWithShopifyString(cents, format) {
        if (typeof cents === 'string') cents = cents.replace('.', '');
        const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
        const value = parseFloat(cents) / 100.0;
        const formatNumber = (num, precision, thousands, decimal) => {
          precision = isNaN(precision) ? 2 : precision;
          thousands = thousands || ',';
          decimal = decimal || '.';
          const fixed = (Math.abs(num) || 0).toFixed(precision);
          const parts = fixed.split('.');
          const intPart = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
          const decPart = parts[1] ? decimal + parts[1] : '';
          return intPart + decPart;
        };
        const placeholder = format.match(placeholderRegex);
        if (!placeholder) return format;
        switch (placeholder[1]) {
          case 'amount': return format.replace(placeholderRegex, formatNumber(value, 2));
          case 'amount_no_decimals': return format.replace(placeholderRegex, formatNumber(value, 0));
          case 'amount_with_comma_separator': return format.replace(placeholderRegex, formatNumber(value, 2, '.', ','));
          case 'amount_no_decimals_with_comma_separator': return format.replace(placeholderRegex, formatNumber(value, 0, '.', ','));
          case 'amount_no_decimals_with_space_separator': return format.replace(placeholderRegex, formatNumber(value, 0, ' ', ','));
          case 'amount_with_apostrophe_separator': return format.replace(placeholderRegex, formatNumber(value, 2, "'", '.'));
          default: return format.replace(placeholderRegex, formatNumber(value, 2));
        }
      }

      computeDesktopAnchor() {
        const col = document.querySelector('.product__info-wrapper.grid__item');
        if (!col) return;
        const r = col.getBoundingClientRect();
        this.style.setProperty('--sticky-cta-left', `${Math.round(r.left)}px`);
        this.style.setProperty('--sticky-cta-width', `${Math.round(r.width)}px`);
      }
    }
  );
}
